import * as React from "react";

import { TileHeadingContext, useTileHeadingLevel } from "@/components/ui/tile-heading-context";
import { cn } from "@/lib/utils";

/**
 * The Bento console's one surface.
 *
 * A page is composed of tiles laid on the canvas with a 16 px gap: a tenant-
 * tinted panel, a 16 px radius, a 1 px quiet line, and never a shadow. Tiles
 * compose a page; they do not nest. `tone="identity"` is a course's pale
 * signature tint: it takes the course hue as `hue` (a `COURSE_HUE` value) and
 * declares it as `--tile-hue`.
 *
 * A tile is a `section` named by `aria-labelledby` (its `TileHeader` id) or
 * `aria-label`; pass `as="div"` when it groups content that is not a region.
 * A list tile (`list`) drops its own inset so rule rows run edge to edge; its
 * `TileHeader` keeps the inset.
 */
function Tile({
  className,
  tone = "surface",
  hue,
  list = false,
  as = "section",
  style,
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  ref?: React.Ref<HTMLElement>;
  list?: boolean;
  as?: "section" | "div";
} & (
  | { tone?: "surface"; hue?: never }
  | { tone: "identity"; hue: string }
)) {
  // Both tags take the same attributes; typing on section keeps the ref an HTMLElement.
  const Component = as as "section";
  return (
    <Component
      data-slot="tile"
      data-tone={tone}
      data-list={list || undefined}
      style={tone === "identity" ? ({ ...style, "--tile-hue": hue } as React.CSSProperties) : style}
      className={cn(
        "relative flex min-w-0 flex-col rounded-xl text-card-foreground",
        list
          ? "gap-0 [&>[data-slot=tile-header]]:ps-5 [&>[data-slot=tile-header]]:pe-4 [&>[data-slot=tile-header]]:pt-4 [&>[data-slot=tile-header]]:pb-2.5"
          : "ps-5 pe-4 pt-4 pb-[1.125rem]",
        tone === "surface" && "border bg-card",
        tone === "identity" && "bg-[color-mix(in_oklab,var(--tile-hue)_13%,var(--card))]",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The level every tile heading inside it takes by default. A record page whose
 * identity is already an `h2` under the page frame's `h1` wraps its views in
 * `<TileHeadingLevel as="h3">`, so composed workspaces nest without each one
 * threading a heading prop. An explicit `as` on a `TileHeader` still wins.
 */
function TileHeadingLevel({ as, children }: { as: "h2" | "h3"; children: React.ReactNode }) {
  return <TileHeadingContext.Provider value={as}>{children}</TileHeadingContext.Provider>;
}

/**
 * A tile's heading row: an 18 px title, an optional count, and at most one
 * quiet action at the end. The row keeps a 30 px line so a tile with and
 * without an action align across the bento.
 */
function TileHeader({
  id,
  title,
  count,
  action,
  as,
  className,
  tabIndex,
  ref,
}: {
  id: string;
  ref?: React.Ref<HTMLHeadingElement>;
  title: React.ReactNode;
  count?: React.ReactNode;
  action?: React.ReactNode;
  as?: "h2" | "h3";
  className?: string;
  tabIndex?: number;
}) {
  const contextLevel = useTileHeadingLevel();
  const Heading = as ?? contextLevel;
  const heading = (
    <Heading
      ref={ref}
      id={id}
      tabIndex={tabIndex}
      className="min-w-0 break-words text-section font-semibold text-foreground focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {title}
    </Heading>
  );
  return (
    <div data-slot="tile-header" className={cn("flex min-h-[1.875rem] items-center gap-2", className)}>
      {heading}
      {count != null && (
        <span className="text-ui tabular-nums text-muted-foreground">{count}</span>
      )}
      {action && <div className="ms-auto flex items-center gap-1">{action}</div>}
    </div>
  );
}

export { Tile, TileHeader, TileHeadingLevel };
